import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, isRef as _isRef, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "assistant-chat__welcome"
}
const _hoisted_2 = {
  class: "assistant-chat__orb",
  "aria-hidden": "true"
}
const _hoisted_3 = { class: "assistant-chat__hello" }
const _hoisted_4 = { class: "assistant-chat__suggestions" }
const _hoisted_5 = ["disabled", "onClick"]
const _hoisted_6 = { class: "assistant-chat__suggestion-icon" }
const _hoisted_7 = { class: "assistant-chat__suggestion-text" }
const _hoisted_8 = {
  key: 1,
  class: "assistant-chat__contact"
}
const _hoisted_9 = { class: "assistant-chat__contact-text" }
const _hoisted_10 = {
  key: 0,
  class: "assistant-chat__contact-role"
}
const _hoisted_11 = {
  key: 0,
  class: "assistant-chat__avatar",
  "aria-hidden": "true"
}
const _hoisted_12 = { class: "assistant-chat__stack" }
const _hoisted_13 = {
  key: 1,
  class: "assistant-chat__bubble assistant-chat__bubble--assistant assistant-chat__reply"
}
const _hoisted_14 = { class: "assistant-chat__inbound-bubble" }
const _hoisted_15 = { class: "assistant-chat__inbound-text" }
const _hoisted_16 = ["datetime"]
const _hoisted_17 = ["innerHTML"]
const _hoisted_18 = {
  key: 1,
  class: "assistant-chat__old-draft"
}
const _hoisted_19 = { class: "assistant-chat__old-draft-label" }
const _hoisted_20 = {
  key: 0,
  class: "assistant-chat__old-draft-subject"
}
const _hoisted_21 = { class: "assistant-chat__old-draft-text" }
const _hoisted_22 = {
  key: 4,
  class: "assistant-chat__actions"
}
const _hoisted_23 = {
  key: 0,
  class: "assistant-chat__actions-bar"
}
const _hoisted_24 = { class: "assistant-chat__actions-bulk" }
const _hoisted_25 = {
  key: 2,
  class: "assistant-chat__confirm"
}
const _hoisted_26 = { class: "assistant-chat__confirm-head" }
const _hoisted_27 = { class: "assistant-chat__confirm-title" }
const _hoisted_28 = {
  key: 0,
  class: "assistant-chat__confirm-summary"
}
const _hoisted_29 = {
  key: 1,
  class: "assistant-chat__confirm-items"
}
const _hoisted_30 = { class: "assistant-chat__confirm-name" }
const _hoisted_31 = {
  key: 0,
  class: "assistant-chat__confirm-meta"
}
const _hoisted_32 = {
  key: 0,
  class: "assistant-chat__confirm-expired"
}
const _hoisted_33 = { class: "assistant-chat__confirm-buttons" }
const _hoisted_34 = {
  key: 3,
  class: "assistant-chat__row"
}
const _hoisted_35 = {
  class: "assistant-chat__avatar assistant-chat__avatar--live",
  "aria-hidden": "true"
}
const _hoisted_36 = { class: "assistant-chat__stack" }
const _hoisted_37 = {
  class: "assistant-chat__typing",
  role: "status"
}
const _hoisted_38 = ["placeholder", "disabled", "onKeydown"]
const _hoisted_39 = ["disabled"]
const _hoisted_40 = { class: "assistant-chat__hint" }

import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { RouterLink } from "vue-router";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Button, Icon } from "@/components/ui";
import AgentActionCard from "@/components/agent/AgentActionCard.vue";
import AgentSteps from "@/components/agent/AgentSteps.vue";
import ContactAvatar from "@/components/agent/assistant/ContactAvatar.vue";
import { useAssistantChat } from "@/composables/agent/useAssistantChat";
import { CONTACT_OVERVIEW_ROUTE } from "@/router/routes";
import type { AgentAction, AgentChatMessage, AgentStoredReplyDraft } from "@/dtos/Agent.dto";

const MAX_INPUT_PX = 140;

export default /*@__PURE__*/_defineComponent({
  __name: 'AssistantChat',
  props: {
    roomy: { type: Boolean }
  },
  setup(__props: any) {



const {
  messages,
  pending,
  liveSteps,
  isSending,
  isLoadingThread,
  isThreadWorking,
  send: sendMessage,
  respond,
  busyIds,
  approve,
  reject,
  decideAll,
  now,
  draft,
  replyThread,
} = useAssistantChat();

// ── AI reply draft threads ──────────────────────────────────
const CHANNEL_LABEL = { email: "by email", linkedin: "on LinkedIn" } as const;

const replyContactName = computed(() => replyThread.value?.contact?.name ?? "Your contact");

const contactRole = computed(() => {
  const contact = replyThread.value?.contact;
  if (!contact) return "";
  return [contact.jobTitle, contact.company].filter(Boolean).join(" at ");
});

// The draft's buttons sit on the latest message showing it; earlier ones keep their wording.
const liveDraftMessageId = computed(() => {
  const draftId = replyThread.value?.draft?.id;
  if (!draftId) return null;
  for (let i = messages.value.length - 1; i >= 0; i--) {
    if (messages.value[i].replyDraft?.actionId === draftId) return messages.value[i].id;
  }
  return null;
});

const draftPlainText = (replyDraft: AgentStoredReplyDraft): string => {
  const { text, body } = replyDraft.draft;
  if (body === undefined) return text ?? "";
  return body
    .replace(/<(br|\/p|\/div|\/li)[^>]*>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const formatTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

const SUGGESTIONS = [
  { icon: "mdi:account-clock-outline", text: "Who haven't I followed up with in two weeks?" },
  { icon: "mdi:calendar-week", text: "What meetings do I have this week?" },
  { icon: "mdi:email-edit-outline", text: "Draft a follow-up email to my most recent meeting" },
  { icon: "mdi:briefcase-search-outline", text: "Which open deals haven't moved this month?" },
];

const store = useStore();
const firstName = computed<string>(() => store.getters.account?.data?.firstName ?? "");

const scrollEl = ref<HTMLElement | null>(null);
const inputEl = ref<HTMLTextAreaElement | null>(null);

const isLocked = computed(() => isSending.value || isLoadingThread.value);

const renderMarkdown = (content: string): string =>
  DOMPurify.sanitize(marked.parse(content) as string);

const openActions = (msg: AgentChatMessage): AgentAction[] =>
  (msg.actions ?? []).filter(
    (a) => a.status === "pending" && (!a.expiresAt || new Date(a.expiresAt).getTime() > now.value)
  );

const isDeciding = (msg: AgentChatMessage): boolean =>
  openActions(msg).some((a) => busyIds.value.has(a.id));

const isConfirmExpired = computed(
  () => !!pending.value && new Date(pending.value.expiresAt).getTime() <= now.value
);

const describeItem = (item: Record<string, string | number | null>): string => {
  const role = [item.title, item.company].filter(Boolean).join(" at ");
  return [role, item.location].filter(Boolean).join(" · ");
};

// The message box grows with the text, up to a few lines.
const autoGrow = () => {
  const el = inputEl.value;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_PX)}px`;
};

const send = (text: string) => {
  if (isLocked.value || !text.trim()) return;
  sendMessage(text);
};

const submit = () => {
  if (isLocked.value || !draft.value.trim()) return;
  send(draft.value);
  draft.value = "";
  nextTick(autoGrow);
};

const scrollToBottom = (behavior: ScrollBehavior) => {
  scrollEl.value?.scrollTo({ top: scrollEl.value.scrollHeight, behavior });
};

watch(
  [() => messages.value.length, () => liveSteps.value.length, pending, isSending],
  async () => {
    await nextTick();
    scrollToBottom("smooth");
  },
  { flush: "post" }
);

// Ready to type, at the latest message, whenever the window opens or changes size.
onMounted(() => {
  scrollToBottom("auto");
  autoGrow();
  inputEl.value?.focus();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["assistant-chat", { 'assistant-chat--roomy': __props.roomy }])
  }, [
    _createElementVNode("div", {
      ref_key: "scrollEl",
      ref: scrollEl,
      class: "assistant-chat__body"
    }, [
      (_unref(messages).length === 0 && !_unref(isLoadingThread))
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(Icon), { icon: "mdi:creation" })
            ]),
            _createElementVNode("h3", _hoisted_3, _toDisplayString(firstName.value ? `Hi ${firstName.value}, how can I help?` : "How can I help?"), 1),
            _cache[5] || (_cache[5] = _createElementVNode("p", { class: "assistant-chat__intro" }, " Ask about your contacts, deals and meetings, or have me draft emails, LinkedIn messages and meeting invites. Nothing is sent until you approve it. ", -1)),
            _createElementVNode("div", _hoisted_4, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(SUGGESTIONS, (suggestion) => {
                return _createElementVNode("button", {
                  key: suggestion.text,
                  type: "button",
                  class: "assistant-chat__suggestion",
                  disabled: _unref(isSending),
                  onClick: ($event: any) => (send(suggestion.text))
                }, [
                  _createElementVNode("span", _hoisted_6, [
                    _createVNode(_unref(Icon), {
                      icon: suggestion.icon
                    }, null, 8, ["icon"])
                  ]),
                  _createElementVNode("span", _hoisted_7, _toDisplayString(suggestion.text), 1),
                  _createVNode(_unref(Icon), {
                    icon: "mdi:arrow-top-right",
                    class: "assistant-chat__suggestion-go"
                  })
                ], 8, _hoisted_5)
              }), 64))
            ])
          ]))
        : _createCommentVNode("", true),
      (_unref(replyThread)?.contact)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(ContactAvatar, {
              name: _unref(replyThread).contact.name,
              src: _unref(replyThread).contact.profilePictureUrl,
              size: 44
            }, null, 8, ["name", "src"]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(_unref(RouterLink), {
                to: { name: _unref(CONTACT_OVERVIEW_ROUTE), params: { id: _unref(replyThread).contact.id } },
                class: "assistant-chat__contact-name"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(replyThread).contact.name), 1)
                ]),
                _: 1
              }, 8, ["to"]),
              (contactRole.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(contactRole.value), 1))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(messages), (msg) => {
        return (_openBlock(), _createElementBlock("div", {
          key: msg.id,
          class: _normalizeClass(["assistant-chat__row", `assistant-chat__row--${msg.role}`])
        }, [
          (msg.role !== 'user')
            ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                _createVNode(_unref(Icon), {
                  icon: msg.role === 'error' ? 'mdi:alert-circle-outline' : 'mdi:creation'
                }, null, 8, ["icon"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            (msg.steps?.length)
              ? (_openBlock(), _createBlock(AgentSteps, {
                  key: 0,
                  steps: msg.steps
                }, null, 8, ["steps"]))
              : _createCommentVNode("", true),
            (msg.replyDraft?.inbound)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  _createElementVNode("p", null, [
                    _createTextVNode(_toDisplayString(firstName.value ? `Hey ${firstName.value}, you` : "You") + " got a reply from ", 1),
                    _createElementVNode("strong", null, _toDisplayString(replyContactName.value), 1),
                    _createTextVNode(" " + _toDisplayString(CHANNEL_LABEL[msg.replyDraft.channel]) + ". " + _toDisplayString(replyContactName.value) + " said: ", 1)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(msg.replyDraft.inbound, (inbound, i) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: i,
                      class: "assistant-chat__inbound"
                    }, [
                      _createVNode(ContactAvatar, {
                        name: replyContactName.value,
                        src: _unref(replyThread)?.contact?.profilePictureUrl,
                        size: 24
                      }, null, 8, ["name", "src"]),
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("p", _hoisted_15, _toDisplayString(inbound.text), 1),
                        _createElementVNode("time", {
                          class: "assistant-chat__inbound-time",
                          datetime: inbound.receivedAt
                        }, _toDisplayString(formatTime(inbound.receivedAt)), 9, _hoisted_16)
                      ])
                    ]))
                  }), 128)),
                  _cache[6] || (_cache[6] = _createElementVNode("p", null, "Here's the reply I prepared for you:", -1))
                ]))
              : (_openBlock(), _createElementBlock("div", {
                  key: 2,
                  class: _normalizeClass(["assistant-chat__bubble", `assistant-chat__bubble--${msg.role}`])
                }, [
                  (msg.role === 'assistant')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "assistant-chat__markdown",
                        innerHTML: renderMarkdown(msg.content)
                      }, null, 8, _hoisted_17))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(_toDisplayString(msg.content), 1)
                      ], 64))
                ], 2)),
            (msg.replyDraft)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                  (msg.id === liveDraftMessageId.value && _unref(replyThread)?.draft)
                    ? (_openBlock(), _createBlock(AgentActionCard, {
                        key: 0,
                        action: _unref(replyThread).draft,
                        busy: _unref(busyIds).has(_unref(replyThread).draft.id),
                        now: _unref(now),
                        title: "Suggested reply",
                        "approve-label": "Send",
                        "reject-label": "Dismiss",
                        "quick-reject": "",
                        onApprove: _cache[0] || (_cache[0] = ($event: any) => (_unref(approve)(_unref(replyThread).draft, $event))),
                        onReject: _cache[1] || (_cache[1] = ($event: any) => (_unref(reject)(_unref(replyThread).draft, $event ?? 'Dismissed')))
                      }, null, 8, ["action", "busy", "now"]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_18, [
                        _createElementVNode("span", _hoisted_19, [
                          _createVNode(_unref(Icon), { icon: "mdi:history" }),
                          _cache[7] || (_cache[7] = _createTextVNode(" Earlier version ", -1))
                        ]),
                        (msg.replyDraft.draft.subject)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(msg.replyDraft.draft.subject), 1))
                          : _createCommentVNode("", true),
                        _createElementVNode("p", _hoisted_21, _toDisplayString(draftPlainText(msg.replyDraft)), 1)
                      ]))
                ], 64))
              : _createCommentVNode("", true),
            (msg.actions?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                  (openActions(msg).length > 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                        _createElementVNode("span", null, [
                          _createVNode(_unref(Icon), { icon: "mdi:hand-back-right-outline" }),
                          _createTextVNode(" " + _toDisplayString(openActions(msg).length) + " drafts need your approval ", 1)
                        ]),
                        _createElementVNode("div", _hoisted_24, [
                          _createVNode(_unref(Button), {
                            variant: "ghost",
                            size: "sm",
                            disabled: isDeciding(msg),
                            onClick: ($event: any) => (_unref(decideAll)(openActions(msg), 'reject'))
                          }, {
                            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                              _createTextVNode(" Reject all ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled", "onClick"]),
                          _createVNode(_unref(Button), {
                            variant: "primary",
                            size: "sm",
                            disabled: isDeciding(msg),
                            loading: isDeciding(msg),
                            onClick: ($event: any) => (_unref(decideAll)(openActions(msg), 'approve'))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(" Approve all (" + _toDisplayString(openActions(msg).length) + ") ", 1)
                            ]),
                            _: 2
                          }, 1032, ["disabled", "loading", "onClick"])
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(msg.actions, (action) => {
                    return (_openBlock(), _createBlock(AgentActionCard, {
                      key: action.id,
                      action: action,
                      busy: _unref(busyIds).has(action.id),
                      now: _unref(now),
                      onApprove: ($event: any) => (_unref(approve)(action, $event)),
                      onReject: ($event: any) => (_unref(reject)(action, $event))
                    }, null, 8, ["action", "busy", "now", "onApprove", "onReject"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ])
        ], 2))
      }), 128)),
      (_unref(pending))
        ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
            _createElementVNode("div", _hoisted_26, [
              _createVNode(_unref(Icon), { icon: "mdi:shield-check-outline" }),
              _cache[9] || (_cache[9] = _createElementVNode("span", null, "Confirm before I go ahead", -1))
            ]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(pending).actions, (item, i) => {
              return (_openBlock(), _createElementBlock("div", {
                key: i,
                class: "assistant-chat__confirm-action"
              }, [
                _createElementVNode("p", _hoisted_27, _toDisplayString(item.title), 1),
                (item.summary)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(item.summary), 1))
                  : _createCommentVNode("", true),
                (item.items?.length)
                  ? (_openBlock(), _createElementBlock("ul", _hoisted_29, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.items, (entry, j) => {
                        return (_openBlock(), _createElementBlock("li", { key: j }, [
                          _createElementVNode("span", _hoisted_30, _toDisplayString(entry.name), 1),
                          (describeItem(entry))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_31, _toDisplayString(describeItem(entry)), 1))
                            : _createCommentVNode("", true)
                        ]))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true)
              ]))
            }), 128)),
            (isConfirmExpired.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_32, " This confirmation expired. Ask again to get a fresh one. "))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_33, [
              _createVNode(_unref(Button), {
                variant: "outline",
                size: "sm",
                disabled: _unref(isSending),
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(respond)(false)))
              }, {
                default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                  _createTextVNode(" Cancel ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_unref(Button), {
                variant: "primary",
                size: "sm",
                disabled: _unref(isSending) || isConfirmExpired.value,
                onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(respond)(true)))
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode(" Confirm ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]))
        : _createCommentVNode("", true),
      (_unref(isSending) || _unref(isLoadingThread))
        ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
            _createElementVNode("span", _hoisted_35, [
              _createVNode(_unref(Icon), { icon: "mdi:creation" })
            ]),
            _createElementVNode("div", _hoisted_36, [
              (_unref(isSending) && _unref(liveSteps).length)
                ? (_openBlock(), _createBlock(AgentSteps, {
                    key: 0,
                    steps: _unref(liveSteps),
                    live: ""
                  }, null, 8, ["steps"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_37, [
                _cache[12] || (_cache[12] = _createElementVNode("span", {
                  class: "assistant-chat__dots",
                  "aria-hidden": "true"
                }, [
                  _createElementVNode("i"),
                  _createElementVNode("i"),
                  _createElementVNode("i")
                ], -1)),
                _createElementVNode("span", null, _toDisplayString(_unref(isThreadWorking)
                  ? "Still working on this…"
                  : _unref(isLoadingThread)
                    ? "Loading conversation…"
                    : "Thinking…"), 1)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ], 512),
    _createElementVNode("form", {
      class: "assistant-chat__composer",
      onSubmit: _withModifiers(submit, ["prevent"])
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["assistant-chat__input-wrap", { 'is-disabled': isLocked.value }])
      }, [
        _withDirectives(_createElementVNode("textarea", {
          ref_key: "inputEl",
          ref: inputEl,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(draft) ? (draft).value = $event : null)),
          class: "assistant-chat__input",
          rows: "1",
          maxlength: "4000",
          placeholder: 
            _unref(replyThread)
              ? 'Ask me to change the reply, or anything about it…'
              : 'Ask anything, or have me draft a follow-up…'
          ,
          "aria-label": "Message the CRM assistant",
          disabled: isLocked.value,
          onInput: autoGrow,
          onKeydown: _withKeys(_withModifiers(submit, ["exact","prevent"]), ["enter"])
        }, null, 40, _hoisted_38), [
          [_vModelText, _unref(draft)]
        ]),
        _createElementVNode("button", {
          type: "submit",
          class: "assistant-chat__send",
          "aria-label": "Send",
          title: "Send (Enter)",
          disabled: isLocked.value || !_unref(draft).trim()
        }, [
          _createVNode(_unref(Icon), { icon: "mdi:arrow-up" })
        ], 8, _hoisted_39)
      ], 2),
      _createElementVNode("p", _hoisted_40, [
        _createVNode(_unref(Icon), { icon: "mdi:shield-lock-outline" }),
        _cache[13] || (_cache[13] = _createTextVNode(" Nothing is sent without your approval. AI can make mistakes. ", -1))
      ])
    ], 32)
  ], 2))
}
}

})